import { useEffect, useState } from 'react'
import type { MainImageRef } from '@cms/contracts'
import { findAssetByPath } from '../api/asset-lookup.js'
import { mediaFileUrl } from '../api/library.js'
import { isLibraryAddress, thumbnailSource, type ThumbSize } from '../domain/thumbnail.js'
import { libraryPathFromValue } from '../domain/media.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { Icon, cx } from '../ui/primitives.js'
import type { IconName } from '../ui/icons.js'

/**
 * The main image of a content or a page, small, in a box of fixed size.
 *
 * The box is always there — the picture, or the icon of the type when there is
 * none or it fails to load — so rows keep the same height and the page does
 * not jump when images arrive. `image` comes from the list read model (already
 * resolved to a library asset or not); `path` is for live data an editor is
 * changing right now, resolved here with one lookup shared by the whole page.
 * `alt` empty (the default) marks the picture as decoration: the row already
 * says the title; pass a text where the picture is the only thing to read.
 */
export function Thumb({ image, path, icon, size = 'sm', alt = '', className, domain }: {
  /** The public domain of the item's site, when it is not the site being worked on (global search). */
  domain?: string | null
  image?: MainImageRef | null
  path?: string | null
  icon: IconName
  size?: ThumbSize
  alt?: string
  className?: string
}) {
  const { siteId, site } = useSiteWorkspace()
  const [assetId, setAssetId] = useState<string | null>(image?.assetId ?? null)
  const [failed, setFailed] = useState(false)
  const address = image?.path ?? path ?? null
  // A library address given without its asset must be looked up before anything is
  // loaded: trying the public site first would fail, and a failed picture stays a
  // placeholder. Until the lookup answers, the box shows the icon.
  const needsLookup = !image && !!address && isLibraryAddress(address)
  const [looked, setLooked] = useState(false)

  useEffect(() => {
    setFailed(false)
    setAssetId(image?.assetId ?? null)
    setLooked(false)
    if (!needsLookup || !address || !siteId) return
    const key = libraryPathFromValue(address)
    if (!key) return
    let cancelled = false
    const timer = window.setTimeout(() => {
      void findAssetByPath(siteId, key).then((found) => {
        if (cancelled) return
        if (found) setAssetId(found.id)
        setLooked(true)
      })
    }, 250)
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [address, image?.assetId, siteId, needsLookup])

  const waiting = needsLookup && !looked
  const source = failed || waiting ? null : thumbnailSource(address ? { path: address, assetId } : null, { domain: domain ?? site?.primaryDomain, fileUrl: mediaFileUrl })
  return <span className={cx('thumb', `thumb-${size}`, !source && 'thumb-empty', className)}>
    {source
      ? <img src={source} alt={alt} loading="lazy" decoding="async" onError={() => setFailed(true)}/>
      : <Icon name={icon} size={size === 'lg' ? 32 : 24}/>}
  </span>
}
